import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, ArrowLeft, LogOut, CheckCircle, XCircle, Loader2, ExternalLink, AlertCircle, Lock,
} from 'lucide-react';
import api from '../utils/api';

/**
 * AdminAudit.js — Admin-only Solana audit-trail tester.
 *
 * Mounted at /admin-portal-7x9k2m/audit. Gated behind the `adminAuditPanel`
 * feature flag — an admin must enable it in AdminConfig before this page
 * renders anything other than a "feature disabled" notice.
 *
 * Lets an admin paste a report, fetch the on-chain record via the admin-only
 * /api/admin/reports/:id/verify endpoint, and confirm the SHA-256 hash matches.
 *
 * Reuses the canonical-string format: reportId + "|" + content.trim() + "|" + completedAt.toISOString()
 *
 * Note: this page is for testing the audit pipeline before enabling the
 * user-visible Solana badge / public verifier. It does NOT expose anything
 * to non-admin users.
 */

async function computeHash(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const AdminAudit = () => {
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(null); // null = loading, true/false = resolved
  const [solanaStatus, setSolanaStatus] = useState(null);
  const [reportId, setReportId] = useState('');
  const [reportText, setReportText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const features = await api.admin.getFeatures();
        setFeatureEnabled(features?.adminAuditPanel === true);
      } catch (err) {
        console.error('Failed to fetch features:', err.message);
        setFeatureEnabled(false);
      }
      try {
        const status = await api.admin.getSolanaStatus();
        setSolanaStatus(status);
      } catch (err) {
        console.error('Failed to fetch Solana status:', err.message);
      }
    })();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const id = reportId.trim().toUpperCase();
    const text = reportText.trim();

    if (!id) { setError('Please enter a Report ID (e.g. DD-1234567890-ABCD1234)'); return; }
    if (!text) { setError('Please paste the full report text'); return; }

    setLoading(true);
    try {
      const record = await api.admin.verifyReport(id);

      if (!record.auditHash) {
        setResult({ status: 'no_audit', record });
        return;
      }

      const completedAt = new Date(record.completedAt).toISOString();
      const canonical = `${id}|${text}|${completedAt}`;
      const localHash = await computeHash(canonical);

      const matches = localHash === record.auditHash;
      setResult({ status: matches ? 'match' : 'mismatch', record, localHash, matches });
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Solana Audit Trail Tester</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 flex-wrap">
            <Link to="/admin-portal-7x9k2m/dashboard" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Dashboard</Link>
            <Link to="/admin-portal-7x9k2m/reports" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Reports</Link>
            <Link to="/admin-portal-7x9k2m/users" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Users</Link>
            <Link to="/admin-portal-7x9k2m/config" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Configuration</Link>
            <Link to="/admin-portal-7x9k2m/vouchers" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Vouchers</Link>
            <Link to="/admin-portal-7x9k2m/reviews" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Reviews</Link>
            <Link to="/admin-portal-7x9k2m/audit" className="px-4 py-3 text-sm font-medium text-white border-b-2 border-red-500">Audit</Link>
            <Link to="/admin-portal-7x9k2m/api-keys" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">API Keys</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {featureEnabled === null && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-red-500 animate-spin" />
          </div>
        )}

        {featureEnabled === false && (
          <div className="bg-gray-800 rounded-lg border border-amber-700/40 p-8 text-center">
            <Lock className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-white mb-2">Audit panel disabled</h2>
            <p className="text-sm text-gray-400 max-w-md mx-auto">
              The <code className="text-amber-300">adminAuditPanel</code> feature flag is OFF.
              Enable it in <Link to="/admin-portal-7x9k2m/config" className="text-red-400 hover:text-red-300 underline">Configuration → Feature Flags</Link> to use this page.
            </p>
            <p className="text-xs text-gray-500 mt-4">
              This panel is for testing the Solana audit pipeline before exposing it to users.
              It does not affect normal report generation.
            </p>
          </div>
        )}

        {featureEnabled === true && (
          <>
            <div className="mb-6 p-4 rounded-lg bg-amber-900/30 border border-amber-700/50 text-amber-200 text-sm flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Admin-only test panel.</strong> This page calls the admin-only verify endpoint
                and is not visible to users. Use it to confirm the on-chain audit hash matches a report's
                content before enabling the public Solana badge.
              </div>
            </div>

            {solanaStatus && (
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
                <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="text-purple-400">◎</span> Solana Audit Wallet Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-400">Cluster: </span>
                    <span className={`font-mono ${solanaStatus.cluster === 'devnet' ? 'text-amber-300' : 'text-green-300'}`}>
                      {solanaStatus.cluster}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">On-chain writes: </span>
                    <span className={solanaStatus.auditEnabled ? 'text-green-300' : 'text-gray-500'}>
                      {solanaStatus.auditEnabled ? 'ENABLED (solanaAuditEnabled = true)' : 'DISABLED (hashes computed but not written)'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Wallet: </span>
                    <span className={solanaStatus.walletConfigured ? 'text-green-300' : 'text-amber-300'}>
                      {solanaStatus.walletConfigured ? 'CONFIGURED' : `MISSING (${solanaStatus.envKeyName})`}
                    </span>
                  </div>
                  {solanaStatus.walletPubkey && (
                    <div className="md:col-span-2">
                      <span className="text-gray-400">Public key: </span>
                      <code className="text-xs text-gray-200 font-mono break-all">{solanaStatus.walletPubkey}</code>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <span className="text-gray-400">RPC: </span>
                    <code className="text-xs text-gray-300 font-mono">{solanaStatus.rpcUrl}</code>
                  </div>
                </div>
                {solanaStatus.cluster === 'devnet' && !solanaStatus.walletConfigured && (
                  <p className="text-xs text-amber-300 mt-3">
                    To test on devnet: generate a keypair, fund it via the <a href="https://faucet.solana.com/" target="_blank" rel="noopener noreferrer" className="underline">devnet faucet</a>,
                    set <code>SOLANA_AUDIT_PRIVATE_KEY_DEVNET</code> in <code>.env</code>, restart backend.
                  </p>
                )}
              </div>
            )}

            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
              <h2 className="text-lg font-semibold text-white mb-1">Report Integrity Verifier</h2>
              <p className="text-sm text-gray-400 mb-4">
                Canonical string hashed: <code className="text-xs text-gray-300">reportId | content.trim() | completedAt.toISOString()</code>
              </p>

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Report ID</label>
                  <input
                    type="text"
                    value={reportId}
                    onChange={e => setReportId(e.target.value)}
                    placeholder="DD-1776848175245-B47AF2E3"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md font-mono text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Report Text</label>
                  <textarea
                    value={reportText}
                    onChange={e => setReportText(e.target.value)}
                    placeholder="Paste the complete report content (matching the saved report.content field)..."
                    rows={10}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white font-mono resize-y focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-300 text-sm bg-red-900/40 border border-red-700/50 p-3 rounded-md">
                    <XCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Verifying...</>
                  ) : (
                    <><Shield className="h-4 w-4" /> Verify Report</>
                  )}
                </button>
              </form>
            </div>

            {result && (
              <div className={`rounded-lg border p-6 ${
                result.status === 'match'
                  ? 'bg-green-900/30 border-green-700/50'
                  : result.status === 'mismatch'
                  ? 'bg-red-900/30 border-red-700/50'
                  : 'bg-amber-900/30 border-amber-700/50'
              }`}>
                {result.status === 'match' && (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle className="h-7 w-7 text-green-400" />
                      <div>
                        <h3 className="text-base font-bold text-green-200">Hash matched ✓</h3>
                        <p className="text-green-300 text-sm">
                          The pasted text reproduces the recorded audit hash. The report is unaltered.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-400">Subject:</span><span className="text-gray-100 font-medium">{result.record.subject || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">Completed:</span><span className="text-gray-100 font-medium">{new Date(result.record.completedAt).toLocaleString()}</span></div>
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-400">SHA-256 fingerprint:</span>
                        <code className="bg-green-900/40 border border-green-800/50 px-2 py-1 rounded text-xs break-all font-mono text-green-200">{result.record.auditHash}</code>
                      </div>
                      {result.record.solanaAuditTx && (
                        <div className="pt-2 flex flex-col gap-1">
                          <span className="text-gray-400">Solana tx:</span>
                          <code className="text-xs text-gray-200 font-mono break-all">{result.record.solanaAuditTx}</code>
                          {result.record.explorerUrl && (
                            <a
                              href={result.record.explorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-purple-400 hover:text-purple-300 text-xs mt-1"
                            >
                              <span>◎</span> View on Solana explorer
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      )}
                      {!result.record.solanaAuditTx && (
                        <div className="pt-2 text-xs text-gray-400 italic">
                          Hash computed but no Solana transaction recorded yet
                          (set <code>SOLANA_AUDIT_PRIVATE_KEY</code> + enable <code>solanaAuditEnabled</code> to write on-chain).
                        </div>
                      )}
                    </div>
                  </>
                )}

                {result.status === 'mismatch' && (
                  <div className="flex items-start gap-3">
                    <XCircle className="h-7 w-7 text-red-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-red-200">Hash mismatch</h3>
                      <p className="text-red-300 text-sm mb-3">
                        The pasted text does not produce the recorded audit hash. The content has been
                        modified, was copied incompletely, or whitespace differs.
                      </p>
                      <div className="space-y-1 text-xs font-mono">
                        <div><span className="text-gray-400">Expected: </span><code className="break-all text-gray-200">{result.record.auditHash}</code></div>
                        <div><span className="text-gray-400">Computed: </span><code className="break-all text-gray-200">{result.localHash}</code></div>
                      </div>
                    </div>
                  </div>
                )}

                {result.status === 'no_audit' && (
                  <div className="flex items-center gap-3">
                    <Shield className="h-7 w-7 text-amber-400" />
                    <div>
                      <h3 className="text-base font-bold text-amber-200">No audit record</h3>
                      <p className="text-amber-300 text-sm">
                        The report exists but has no Solana audit hash. Either it was generated before
                        the audit feature was enabled, or <code>solanaAuditEnabled</code> is OFF.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminAudit;
